/**
 * Single source of truth for tRPC procedures that require NO Clerk session.
 *
 * Consumed by two places that MUST agree, or anonymous access silently breaks:
 * - `src/middleware.ts` — the edge allowlist. A batched `/api/trpc/a,b` request
 *   is allowed through only when EVERY procedure in it is in this set.
 * - `src/lib/trpc/react.tsx` — the client transport split routes these onto a
 *   dedicated, guard-free batch so `signedOutGuardLink` doesn't terminate them
 *   client-side for logged-out visitors, and so they never share a batch with
 *   protected procedures (which would 401 the whole batch in middleware).
 *
 * Edge-safe: plain string constants only, no runtime imports.
 *
 * When you add a new `publicProcedure` / `anonymousClaimMutationProcedure`,
 * add its path here — that single edit covers both the edge allowlist and the
 * client split. (Drift between the two is how anonymous share pages break.)
 */
export const PUBLIC_TRPC_PROCEDURES = new Set<string>([
  "publicYcApp.scoreAndCoach",
  "publicYcApp.getStatus",
  "publicYcApp.getCurrentApp",
  "publicYcApp.captureEmail",
  "publicYcApp.linkOnSignup",
  "publicYcApp.updateContent",
  "publicYcApp.saveSuggestionAnswer",
  "publicYcApp.saveOneLinerSelection",
  "share.getProjectSnapshot",
  "share.getBlockContent",
  "share.getReportContent",
  "share.getReportsCollectionContent",
  "share.getReportsCollectionReportContent",
  // Anonymous weekly-report viewer comments (read + create). publicProcedure,
  // capability-cookie gated — must be allowlisted or the edge 401s them.
  "share.listReportComments",
  "share.addReportComment",
  // Investor-updates collection share (mirror of the weekly collection).
  "share.getInvestorUpdatesCollectionContent",
  "share.getInvestorUpdatesCollectionUpdateContent",
  // Single investor-update share (mirror of the per-report weekly share).
  "share.getInvestorUpdateContent",
  "share.listInvestorUpdateComments",
  "share.addInvestorUpdateComment",
  "share.getEventsSince",
  "share.listShareCommentsForToken",
  "share.addComment",
  "share.setViewerCommentThreadHidden",
  "share.relocateShareComment",
  "labs.vcPitchEvaluation.pitches.getByShareToken",
  "anonymousProject.getSnapshot",
  "anonymousProject.getEventsSince",
  "anonymousProject.getOnboardingHistory",
  // Claim-cookie-authed WRITES (LEV-1584): auth lives in the
  // anonymousClaimMutationProcedure tier, not Clerk. A new anonymousProject.*
  // procedure MUST be added here or the edge middleware 401s it before tRPC
  // runs — caught live on staging when approve shipped without these.
  "anonymousProject.approve",
  "anonymousProject.generateDeliverable",
  "anonymousProject.chooseIcp",
  "anonymousProject.patchUIState",
]);

/** True when the tRPC procedure path requires no Clerk session. */
export function isPublicProcedurePath(path: string): boolean {
  return PUBLIC_TRPC_PROCEDURES.has(path);
}
